import {useEffect, useMemo, useState} from 'react'
import {AlertTriangle, Plus, Settings2, Trash2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardDescription, CardTitle} from '@/components/ui/card'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table'
import {Checkbox} from '@/components/ui/checkbox'
import {Input} from '@/components/ui/input'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {EnumSelectItems} from '@/components/shared/EnumSelectItems'
import {Combobox} from '@/components/ui/combobox'
import type {ComboboxOption} from '@/components/ui/combobox'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {MutedText} from '@/components/shared/MutedText'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {TransformParamsFields} from '@/components/shared/TransformParamsFields'
import {validateTransformArgs} from '@/components/shared/transform-args'
import {MappingType, MappingTypeBadge} from '@/types/outbound-connector'
import type {OutboundConnectorAttributeMapping} from '@/types/outbound-connector'
import {TargetEntityType, AttributeMappingTransformBadge} from '@/types/inbound-connector'
import {useAttributeDefinitions} from '../hooks/useAttributeDefinitions'
import {AttributeDefinitionResponseFields} from '@/types/attribute-definition'
import {UserWithRolesResponseFields} from '@/types/roles'

const RFC4512_PATTERN = /^[a-zA-Z][a-zA-Z0-9-.]*$/

/**
 * SCIM 2.0 target path allowlist.
 * Simple top-level attrs + standard complex sub-attrs from RFC 7643.
 */
const SCIM_ALLOWED_TARGETS = new Set([
    'userName',
    AttributeDefinitionResponseFields.displayName,
    AttributeDefinitionResponseFields.active,
    'emails',
    'phoneNumbers',
    'photos',
    'addresses',
    'groups',
    'entitlements',
    UserWithRolesResponseFields.roles,
    'x509Certificates',
    'name.formatted',
    'name.familyName',
    'name.givenName',
    'name.middleName',
    'name.honorificPrefix',
    'name.honorificSuffix',
    'addresses.formatted',
    'addresses.streetAddress',
    'addresses.locality',
    'addresses.region',
    'addresses.postalCode',
    'addresses.country',
    'addresses.type',
    'addresses.primary',
    'emails.value',
    'emails.type',
    'emails.primary',
    'phoneNumbers.value',
    'phoneNumbers.type',
    'phoneNumbers.primary',
    'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:employeeNumber',
    'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:organization',
    'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:department',
    'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:manager',
    'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:division',
    'urn:ietf:params:scim:schemas:extension:enterprise:2.0:User:costCenter',
])

const CONNECTOR_TYPE_SCIM = 'SCIM_2_0'
const MAX_MAPPINGS = 50
const MAX_TARGET_LENGTH = 100
const MAX_SOURCE_LENGTH = 500
const TRANSFORM_NONE = '__none__'

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

interface MappingErrors {
    source?: string
    target?: string
    transformArgs?: string
}

function validateTarget(
    target: string,
    allTargets: string[],
    idx: number,
    connectorType: string,
    i18n: ReturnType<typeof useI18n>,
): string | undefined {
    if (!target) return undefined
    if (target.length > MAX_TARGET_LENGTH) return i18n.connectors.attributeMappings.targetExceedsMaxLength
    if (!RFC4512_PATTERN.test(target)) return i18n.connectors.attributeMappings.invalidRfc4512Target
    const duplicateIndex = allTargets.findIndex((t, i) => i !== idx && t === target)
    if (duplicateIndex !== -1) {
        return i18n.connectors.attributeMappings.duplicateTarget({name: target})
    }
    if (connectorType === CONNECTOR_TYPE_SCIM && !SCIM_ALLOWED_TARGETS.has(target)) {
        return i18n.connectors.attributeMappings.unknownScimTarget
    }
    return undefined
}

function validateSource(
    source: string,
    type: string | null,
    knownNames: Set<string>,
    i18n: ReturnType<typeof useI18n>,
): string | undefined {
    if (!source) return i18n.connectors.attributeMappings.sourceRequired
    if (source.length > MAX_SOURCE_LENGTH) return i18n.connectors.attributeMappings.sourceExceedsMaxLength

    if (type === MappingType.DIRECT) {
        if (!knownNames.has(source)) return i18n.connectors.attributeMappings.unknownSource
    }

    if (type === MappingType.COMPUTED) {
        // Validate template syntax: no nested/unclosed braces
        const syntaxError = validateComputedTemplate(source, knownNames, i18n)
        if (syntaxError) return syntaxError
    }

    return undefined
}

function validateComputedTemplate(
    template: string,
    knownNames: Set<string>,
    i18n: ReturnType<typeof useI18n>,
): string | undefined {
    // Check for nested {{ or }}
    if (/\{\{/.test(template) || /\}\}/.test(template)) {
        return i18n.connectors.attributeMappings.invalidTemplateSyntax
    }
    // Check for empty braces {}
    if (/\{\}/.test(template)) {
        return i18n.connectors.attributeMappings.invalidTemplateSyntax
    }
    // Check for unclosed braces
    let depth = 0
    for (const ch of template) {
        if (ch === '{') depth++
        else if (ch === '}') depth--
        if (depth < 0) return i18n.connectors.attributeMappings.invalidTemplateSyntax
    }
    if (depth !== 0) return i18n.connectors.attributeMappings.invalidTemplateSyntax

    // Validate variable names
    const vars = [...template.matchAll(/\{([^{}]+)\}/g)].map((m) => m[1])
    for (const v of vars) {
        if (v && !knownNames.has(v)) {
            return i18n.connectors.attributeMappings.unknownVariable({name: v})
        }
    }
    return undefined
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AttributeMappingEditorProps {
    connectorId: string
    connectorType: string
    attributeMappings: OutboundConnectorAttributeMapping[] | null
    canUpdate: boolean
    onDirtyChange?: (dirty: boolean) => void
    onSaved?: () => void
}

// ---------------------------------------------------------------------------
// Warning banner — shared between edit and read-only modes
// ---------------------------------------------------------------------------

function MappingWarningBanner({action, testId}: {action?: React.ReactNode; testId?: string}) {
    const i18n = useI18n()
    return (
        <InfoBanner
            icon={AlertTriangle}

            className="rounded-lg border border-warning/40 bg-warning/10 text-foreground"
            data-testid={testId}
        >
            <div>
                <p className="font-medium text-sm">{i18n.connectors.attributeMappings.noMappings}</p>
                <MutedText size="sm" className="mt-1">{i18n.connectors.attributeMappings.noMappingsWarning}</MutedText>
            </div>
            {action}
        </InfoBanner>
    )
}

// ---------------------------------------------------------------------------
// Read-only component (viewer/auditor)
// ---------------------------------------------------------------------------

interface AttributeMappingReadOnlyProps {
    attributeMappings: OutboundConnectorAttributeMapping[] | null
}

export function AttributeMappingReadOnly({attributeMappings}: AttributeMappingReadOnlyProps) {
    const i18n = useI18n()

    if (!attributeMappings || attributeMappings.length === 0) {
        return <MappingWarningBanner testId="readonly-warning-banner"/>
    }

    return (
        <div className="space-y-2" data-testid="attribute-mapping-readonly">
            {attributeMappings.map((m, idx) => (
                <div key={idx} className="flex items-center gap-4 py-2 border-b last:border-b-0 text-sm" data-testid={`readonly-row-${idx}`}>
                    <MutedText size="sm" className="w-20 shrink-0" data-testid={`readonly-type-${idx}`}>{m.type}</MutedText>
                    <span className="flex-1 font-mono" data-testid={`readonly-source-${idx}`}>{m.source}</span>
                    <MutedText size="sm">→</MutedText>
                    <span className="flex-1 font-mono" data-testid={`readonly-target-${idx}`}>{m.target}</span>
                    {m.transform && (
                        <MutedText size="xs" className="font-mono">{m.transform}</MutedText>
                    )}
                    {m.required && (
                        <MutedText size="xs" data-testid={`readonly-required-${idx}`}>
                            {i18n.connectors.attributeMappings.required}
                        </MutedText>
                    )}
                </div>
            ))}
        </div>
    )
}

// ---------------------------------------------------------------------------
// Editor component
// ---------------------------------------------------------------------------

export function AttributeMappingEditor({
    connectorId,
    connectorType,
    attributeMappings,
    canUpdate,
    onDirtyChange,
    onSaved,
}: AttributeMappingEditorProps) {
    const i18n = useI18n()
    const services = useServices()
    const definitions = useAttributeDefinitions()

    const knownNames = useMemo(() => new Set(definitions.map((d) => d.name)), [definitions])

    // Combobox options from definitions
    const comboboxOptions: ComboboxOption[] = useMemo(
        () => definitions.map((d) => ({value: d.name, label: d.label})),
        [definitions],
    )

    // Saved state (source of truth)
    const [savedMappings, setSavedMappings] = useState<OutboundConnectorAttributeMapping[]>(
        attributeMappings ?? [],
    )
    // Current (editing) state
    const [currentMappings, setCurrentMappings] = useState<OutboundConnectorAttributeMapping[]>(
        attributeMappings ?? [],
    )
    // Per-row combobox search state (DIRECT source)
    const [searchQueries, setSearchQueries] = useState<string[]>(() =>
        Array(attributeMappings?.length ?? 0).fill(''),
    )

    const isDirty = useMemo(
        () => JSON.stringify(currentMappings) !== JSON.stringify(savedMappings),
        [currentMappings, savedMappings],
    )

    // Notify parent of dirty state
    useEffect(() => {
        onDirtyChange?.(isDirty)
    }, [isDirty, onDirtyChange])

    // ---------------------------------------------------------------------------
    // Validation
    // ---------------------------------------------------------------------------
    const allTargets = currentMappings.map((m) => m.target)

    const rowErrors: MappingErrors[] = useMemo(
        () =>
            currentMappings.map((m, idx) => ({
                target: validateTarget(m.target, allTargets, idx, connectorType, i18n),
                source: validateSource(m.source, m.type, knownNames, i18n),
                transformArgs: validateTransformArgs(m.transform, m.transformArgs, i18n),
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentMappings, connectorType, knownNames],
    )

    const hasErrors = rowErrors.some((e) => e.source || e.target || e.transformArgs)

    // ---------------------------------------------------------------------------
    // Row management
    // ---------------------------------------------------------------------------
    const addMapping = () => {
        if (currentMappings.length >= MAX_MAPPINGS) return
        const newMapping: OutboundConnectorAttributeMapping = {
            source: '',
            target: '',
            targetEntityType: TargetEntityType.IDENTITY,
            identifierType: null,
            transform: null,
            type: MappingType.DIRECT,
            required: false,
            transformArgs: null,
        }
        setCurrentMappings((prev) => [...prev, newMapping])
        setSearchQueries((prev) => [...prev, ''])
    }

    const removeMapping = (idx: number) => {
        setCurrentMappings((prev) => prev.filter((_, i) => i !== idx))
        setSearchQueries((prev) => prev.filter((_, i) => i !== idx))
    }

    const updateMapping = (idx: number, updates: Partial<OutboundConnectorAttributeMapping>) => {
        setCurrentMappings((prev) =>
            prev.map((m, i) => (i === idx ? {...m, ...updates} : m)),
        )
    }

    const handleTypeChange = (idx: number, newType: string) => {
        updateMapping(idx, {type: newType as MappingType, source: ''})
    }

    const handleSearch = (idx: number, query: string) => {
        setSearchQueries((prev) => prev.map((q, i) => (i === idx ? query : q)))
    }

    // ---------------------------------------------------------------------------
    // Mutation
    // ---------------------------------------------------------------------------
    const mutation = useEntityMutation({
        mutationFn: (data: OutboundConnectorAttributeMapping[] | null) =>
            services.outboundConnectors.updateAttributeMappings(connectorId, {attributeMappings: data}),
        invalidate: ['connector', connectorId],
        successMessage: currentMappings.length > 0
            ? i18n.connectors.attributeMappings.saved
            : i18n.connectors.attributeMappings.cleared,
        onSuccess: () => {
            setSavedMappings(currentMappings)
            onSaved?.()
        },
    })

    const handleSave = () => {
        if (hasErrors || mutation.isPending) return
        const payload = currentMappings.length > 0 ? currentMappings : null
        mutation.mutate(payload)
    }

    const handleCancel = () => {
        setCurrentMappings(savedMappings)
        setSearchQueries(Array(savedMappings.length).fill(''))
    }

    const atMaxMappings = currentMappings.length >= MAX_MAPPINGS
    const isEmpty = currentMappings.length === 0

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    // Read-only path
    if (!canUpdate) {
        return <AttributeMappingReadOnly attributeMappings={savedMappings.length > 0 ? savedMappings : null}/>
    }

    return (
        <div data-testid="attribute-mapping-editor">
            {/* Warning banner when no mappings — the footer Add button is the single entry point */}
            {isEmpty && (
                <MappingWarningBanner
                    testId="mapping-warning-banner"
                />
            )}

            {/* Mapping table — overflow-visible override so Combobox dropdown is not clipped */}
            {!isEmpty && (
                <div className="[&>div]:overflow-visible"> <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-28" data-testid="col-type">{i18n.connectors.attributeMappings.type}</TableHead>
                            <TableHead data-testid="col-source">{i18n.connectors.attributeMappings.source}</TableHead>
                            <TableHead data-testid="col-target">{i18n.connectors.attributeMappings.target}</TableHead>
                            <TableHead className="w-44" data-testid="col-transform">{i18n.connectors.attributeMappings.transform}</TableHead>
                            <TableHead className="w-20 text-center" data-testid="col-required">{i18n.connectors.attributeMappings.required}</TableHead>
                            <TableHead className="w-10" data-testid="col-actions"/>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentMappings.map((mapping, idx) => {
                            const errors = rowErrors[idx] ?? {}
                            const filteredOptions = searchQueries[idx]
                                ? comboboxOptions.filter((o) =>
                                      o.label.toLowerCase().includes(searchQueries[idx].toLowerCase()),
                                  )
                                : comboboxOptions

                            return (
                                <TableRow key={idx} data-testid={`mapping-row-${idx}`}>
                                    <TableCell className="align-top">
                                        <Select
                                            value={mapping.type ?? undefined}
                                            onValueChange={(val) => handleTypeChange(idx, val)}
                                        >
                                            <SelectTrigger data-testid={`select-type-${idx}`} className="h-9">
                                                <SelectValue/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <EnumSelectItems enumClass={MappingTypeBadge}/>
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                    <TableCell className="align-top">
                                        {mapping.type === MappingType.DIRECT ? (
                                            <Combobox
                                                options={filteredOptions}
                                                value={mapping.source || null}
                                                onValueChange={(val) => updateMapping(idx, {source: val ?? ''})}
                                                onSearch={(q) => handleSearch(idx, q)}
                                                placeholder={i18n.connectors.attributeMappings.sourceHintDirect}
                                                emptyMessage={i18n.connectors.attributeMappings.unknownSource}
                                                data-testid={`combobox-source-${idx}`}
                                                className={errors.source ? 'border-destructive' : ''}
                                            />
                                        ) : (
                                            <Input
                                                value={mapping.source}
                                                onChange={(e) => updateMapping(idx, {source: e.target.value})}
                                                placeholder={
                                                    mapping.type === MappingType.COMPUTED
                                                        ? i18n.connectors.attributeMappings.sourceHintComputed
                                                        : i18n.connectors.attributeMappings.sourceHintStatic
                                                }
                                                data-testid={`input-source-${idx}`}
                                                className={errors.source ? 'border-destructive' : ''}
                                            />
                                        )}
                                        {errors.source && (
                                            <MutedText size="xs" className="text-destructive mt-1" data-testid={`error-source-${idx}`}>
                                                {errors.source}
                                            </MutedText>
                                        )}
                                    </TableCell>
                                    <TableCell className="align-top">
                                        <Input
                                            value={mapping.target}
                                            onChange={(e) => updateMapping(idx, {target: e.target.value})}
                                            placeholder={i18n.connectors.attributeMappings.targetHint}
                                            data-testid={`input-target-${idx}`}
                                            className={errors.target ? 'border-destructive' : ''}
                                        />
                                        {errors.target && (
                                            <MutedText size="xs" className="text-destructive mt-1" data-testid={`error-target-${idx}`}>
                                                {errors.target}
                                            </MutedText>
                                        )}
                                    </TableCell>
                                    <TableCell className="align-top">
                                        <Select
                                            value={mapping.transform ?? TRANSFORM_NONE}
                                            onValueChange={(val) =>
                                                updateMapping(idx, {
                                                    transform: val === TRANSFORM_NONE ? null : val,
                                                    transformArgs: null,
                                                })}
                                        >
                                            <SelectTrigger data-testid={`select-transform-${idx}`} className="h-9">
                                                <SelectValue placeholder={i18n.connectors.attributeMappings.transformNone}/>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={TRANSFORM_NONE}>{i18n.connectors.attributeMappings.transformNone}</SelectItem>
                                                <EnumSelectItems enumClass={AttributeMappingTransformBadge}/>
                                            </SelectContent>
                                        </Select>
                                        <TransformParamsFields
                                            transform={mapping.transform}
                                            args={mapping.transformArgs}
                                            onChange={(a) => updateMapping(idx, {transformArgs: a})}
                                            testIdPrefix={`transform-${idx}`}
                                        />
                                        {errors.transformArgs && (
                                            <MutedText size="xs" className="text-destructive mt-1" data-testid={`error-transform-${idx}`}>
                                                {errors.transformArgs}
                                            </MutedText>
                                        )}
                                    </TableCell>
                                    <TableCell className="align-top text-center">
                                        <Checkbox
                                            id={`required-${idx}`}
                                            checked={mapping.required}
                                            onCheckedChange={(checked) =>
                                                updateMapping(idx, {required: checked === true})
                                            }
                                            data-testid={`checkbox-required-${idx}`}
                                            className="mt-2"
                                        />
                                    </TableCell>
                                    <TableCell className="align-top">
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            className="h-9 w-9 p-0 text-muted-foreground hover:text-destructive"
                                            onClick={() => removeMapping(idx)}
                                            data-testid={`btn-remove-mapping-${idx}`}
                                            aria-label={i18n.connectors.attributeMappings.confirmDelete}
                                        >
                                            <Trash2 className="h-4 w-4"/>
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
                </div>
            )}

            {/* Footer: Add button always visible, Save/Cancel visible when dirty */}
            <div className="flex items-center justify-between pt-4">
                <div title={atMaxMappings ? i18n.connectors.attributeMappings.maxMappingsTooltip : undefined}>
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addMapping}
                        disabled={atMaxMappings}
                        data-testid="btn-add-mapping"
                    >
                        <Plus className="h-4 w-4 mr-1"/>
                        {i18n.connectors.attributeMappings.addMappingAction}
                    </Button>
                </div>

                {isDirty && (
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleCancel}
                            disabled={mutation.isPending}
                            data-testid="btn-cancel-mappings"
                        >
                            {i18n.common.cancel}
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            onClick={handleSave}
                            disabled={hasErrors || mutation.isPending}
                            data-testid="btn-save-mappings"
                        >
                            {mutation.isPending ? i18n.common.saving : i18n.common.saveChanges}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    )
}

// ---------------------------------------------------------------------------
// Card wrapper for attribute mapping editor
// ---------------------------------------------------------------------------

interface AttributeMappingCardProps {
    connectorId: string
    connectorType: string
    attributeMappings: OutboundConnectorAttributeMapping[] | null
    canUpdate: boolean
    onDirtyChange?: (dirty: boolean) => void
}

export function AttributeMappingCard({
    connectorId,
    connectorType,
    attributeMappings,
    canUpdate,
    onDirtyChange,
}: AttributeMappingCardProps) {
    const i18n = useI18n()

    return (
        <Card data-testid="attribute-mapping-card">
            <RowCardHeader>
                <div>
                    <CardTitle className="flex items-center gap-2" data-testid="card-title-attribute-mappings">
                        <Settings2 className="h-5 w-5"/>
                        {i18n.connectors.attributeMappings.title}
                    </CardTitle>
                    <CardDescription>
                        {i18n.connectors.attributeMappings.description}
                        <br/>
                        <span>{i18n.connectors.attributeMappings.descriptionDetail}</span>
                    </CardDescription>
                </div>
            </RowCardHeader>
            <CardContent>
                {canUpdate ? (
                    <AttributeMappingEditor
                        connectorId={connectorId}
                        connectorType={connectorType}
                        attributeMappings={attributeMappings}
                        canUpdate={canUpdate}
                        onDirtyChange={onDirtyChange}
                    />
                ) : (
                    <AttributeMappingReadOnly attributeMappings={attributeMappings}/>
                )}
            </CardContent>
        </Card>
    )
}
